import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Shield, Truck, Headphones } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { formatPrice } from '../lib/utils';

const HomePage = () => {
  const [featuredGems, setFeaturedGems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedGems();
  }, []);

  const fetchFeaturedGems = async () => {
    try {
      const response = await api.get('/products?is_featured=true&sort_by=created_at&sort_order=desc');
      setFeaturedGems(response.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch featured gems:', error);
    } finally {
      setLoading(false);
    }
  };

  const gemstones = [
    {
      title: 'Emeralds',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/57qw7w56_F683E346-12CC-4A47-A850-08225E60D944.png',
      type: 'emerald'
    },
    {
      title: 'Rubies',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/qm5wph25_B13B369F-7304-410A-A966-07E267DEAF7E.png',
      type: 'ruby'
    },
    {
      title: 'Sapphires',
      image: 'https://customer-assets.emergentagent.com/job_3b35c559-07b5-4fd8-a210-8ba78c0ad2dc/artifacts/8627k2of_A3D9625E-612F-4417-A540-193F15E44D53.png',
      type: 'sapphire'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-white" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1744822220368-c380740bfc7f?w=1920&q=80"
            alt="Luxury gemstone"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="relative z-10 text-center px-4 max-w-4xl"
        >
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl mb-6 tracking-tight">
            The Gem Atelier
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-600 font-light tracking-wide">
            Curators of Exceptional Gems
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/shop')}
              size="lg"
              className="bg-black text-white hover:bg-gray-900 px-8 py-6 text-base"
              data-testid="shop-gems-button"
            >
              Shop Gems
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate('/birthstones')}
              variant="outline"
              size="lg"
              className="border-black text-black hover:bg-black hover:text-white px-8 py-6 text-base"
            >
              Discover Birthstones
            </Button>
            <Button
              onClick={() => navigate('/request-stone')}
              variant="outline"
              size="lg"
              className="border-black text-black hover:bg-black hover:text-white px-8 py-6 text-base"
            >
              Request a Stone
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Explore By Gemstone - Simple 3 Cards */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Explore By Gemstone</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover our curated collection of the world's finest emeralds, rubies, and sapphires
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {gemstones.map((gem, index) => (
              <motion.div
                key={gem.type}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => navigate(`/shop?gem_type=${gem.type}`)}
                className="group cursor-pointer"
                data-testid={`category-${gem.type}`}
              >
                <div className="aspect-square mb-6 overflow-hidden rounded-lg bg-gray-50">
                  <img
                    src={gem.image}
                    alt={gem.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <h3 className="font-serif text-2xl text-center mb-4">{gem.title}</h3>
                <div className="text-center">
                  <Button
                    variant="outline"
                    className="border-black text-black hover:bg-black hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/shop?gem_type=${gem.type}`);
                    }}
                  >
                    Shop {gem.title}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/blog')}
              variant="link"
              className="text-gray-600 hover:text-black"
            >
              Learn more about our gemstones →
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Gems */}
      <section className="py-24 px-4 bg-gray-50" data-testid="featured-gems-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Featured Selections</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Each gemstone is carefully selected for its exceptional quality and beauty
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 aspect-[3/4] mb-4"></div>
                  <div className="bg-gray-200 h-6 mb-2"></div>
                  <div className="bg-gray-200 h-4 w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredGems.map((gem, index) => (
                <motion.div
                  key={gem.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => navigate(`/products/${gem.id}`)}
                  className="group cursor-pointer"
                  data-testid={`featured-gem-${index}`}
                >
                  <div className="product-image-wrapper aspect-[3/4] mb-4 bg-gray-100">
                    <img
                      src={gem.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=600&q=80'}
                      alt={gem.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-sans text-sm mb-1 group-hover:opacity-60 transition-opacity line-clamp-2">
                    {gem.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {gem.price_on_request ? 'Price on Request' : formatPrice(gem.price, gem.currency)}
                  </p>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/shop')}
              variant="outline"
              size="lg"
              className="border-black text-black hover:bg-black hover:text-white"
              data-testid="view-all-button"
            >
              View All Gems
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Why The Gem Atelier</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {[
              {
                icon: Shield,
                title: 'Authenticity & Certification',
                description: 'Every gemstone comes with certification from leading gemological laboratories',
              },
              {
                icon: Award,
                title: 'Curated High-End Selection',
                description: 'Only the finest gemstones with exceptional quality make it into our collection',
              },
              {
                icon: Truck,
                title: 'Secure Worldwide Shipping',
                description: 'Insured delivery to your doorstep with full tracking',
              },
              {
                icon: Headphones,
                title: 'Expert Support',
                description: 'Our gemologists are available to guide you through every purchase',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="flex justify-center mb-4">
                  <feature.icon className="w-12 h-12" strokeWidth={1} />
                </div>
                <h3 className="font-serif text-xl mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl mb-6">
              Begin Your Journey
            </h2>
            <p className="text-gray-300 mb-8 text-lg max-w-2xl mx-auto">
              Whether you're a collector, designer, or connoisseur, we invite you to explore our exceptional collection
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/shop')}
                size="lg"
                className="bg-white text-black hover:bg-gray-100 px-8 py-6 text-base"
              >
                Explore Collection
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-base"
              >
                Schedule Consultation
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
